// Restaurant Invoice Report — every restaurant/bar order that has been billed
// (invoiced or paid), with guest details, type, amount, GST, payment status and
// whether it was charged to a room or settled at the counter. Date-range +
// search filters, summary totals, and CSV export.
import React, { useEffect, useMemo, useState } from 'react';
import config from '../../config';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
  Chip,
} from '@mui/material';

const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('en-IN');
};

const orderType = (o) => {
  // #12: prefer the real stored order type; fall back to the old heuristic
  // for legacy rows that pre-date the OrderType column.
  if ((o.bookingId || 0) > 0) return 'Room';
  const stored = (o.orderType || '').toString();
  if (stored === 'DineIn') return 'Dine-in';
  if (['Takeaway', 'Pickup', 'Delivery'].includes(stored)) return stored;
  const t = (o.tableno || '').toString();
  if (t === 'Takeaway') return 'Takeaway';
  if (t.startsWith('Pickup')) return 'Pickup';
  if (t.startsWith('Delivery')) return 'Delivery';
  return 'Dine-in';
};

export default function RestaurantInvoiceReport() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const currency = company?.currency ?? '₹';
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    // Dedicated endpoint returns ALL billed orders (room + dine-in + takeaway)
    // with the guest resolved (booking guest or captured walk-in customer).
    fetch(
      `${config.API_BASE_URL}/restaurantreport/invoices/${encodeURIComponent(company?.id)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((res) => res.json())
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const guest = (o.guestName || '').toLowerCase();
      const table = (o.tableno || '').toLowerCase();
      const inv = (o.invNumber || '').toString().toLowerCase();
      const searchOk =
        !q ||
        guest.includes(q) ||
        table.includes(q) ||
        inv.includes(q) ||
        (o.guestMobile || '').includes(q) ||
        String(o.id).includes(q);
      const d = new Date(o.createDate);
      const fromOk = !fromDate || d >= new Date(fromDate);
      const toOk = !toDate || d <= new Date(toDate + 'T23:59:59');
      const typeOk = typeFilter === 'All' || orderType(o) === typeFilter;
      const statusOk =
        statusFilter === 'All' ||
        (statusFilter === 'Cancelled'
          ? !!o.isCancel
          : statusFilter === 'Paid'
            ? !!o.isPaid && !o.isCancel
            : !o.isPaid && !o.isCancel);
      return searchOk && fromOk && toOk && typeOk && statusOk;
    });
  }, [orders, search, fromDate, toDate, typeFilter, statusFilter]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, o) => {
          acc.count += 1;
          if (!o.isCancel) {
            acc.amount += Number(o.finalPrice || 0);
            acc.gst += Number(o.gstAmount || 0);
          }
          return acc;
        },
        { count: 0, amount: 0, gst: 0 },
      ),
    [filtered],
  );

  const exportCsv = () => {
    const rows = [
      [
        'Inv/Order No',
        'Date',
        'Type',
        'Table/Room',
        'Guest',
        'Mobile',
        'Charged To',
        'GST',
        'Final Amount',
        'Status',
      ],
      ...filtered.map((o) => [
        o.invNumber || `ORD_${o.id}`,
        fmtDate(o.createDate),
        orderType(o),
        o.tableno || ((o.bookingId || 0) > 0 ? 'Room' : '-'),
        o.guestName || 'Walk-in',
        o.guestMobile || '-',
        (o.bookingId || 0) > 0 ? 'Room Tab' : 'Counter',
        Number(o.gstAmount || 0).toFixed(2),
        Number(o.finalPrice || 0).toFixed(2),
        o.isCancel ? 'Cancelled' : o.isPaid ? 'Paid' : 'Unpaid',
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'restaurant-invoice-report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant='h4' fontWeight='bold' mb={3}>
        🍽️ Restaurant / Bar Invoice Report
      </Typography>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {[
          ['Invoices', totals.count],
          ['Total Amount', `${currency}${totals.amount.toFixed(2)}`],
          ['Total GST', `${currency}${totals.gst.toFixed(2)}`],
        ].map(([label, val]) => (
          <Paper key={label} sx={{ p: 2, minWidth: 160, flex: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              {label}
            </Typography>
            <Typography variant='h6' fontWeight={700}>
              {val}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}>
        <TextField
          size='small'
          label='Search guest / table / inv no'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          size='small'
          type='date'
          label='From'
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <TextField
          size='small'
          type='date'
          label='To'
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <TextField
          select
          size='small'
          label='Type'
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 130 }}>
          {['All', 'Dine-in', 'Takeaway', 'Pickup', 'Delivery', 'Room'].map(
            (t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ),
          )}
        </TextField>
        <TextField
          select
          size='small'
          label='Status'
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 120 }}>
          {['All', 'Paid', 'Unpaid', 'Cancelled'].map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <Button variant='contained' onClick={exportCsv}>
          Export CSV
        </Button>
      </Box>

      <Paper elevation={3}>
        <TableContainer sx={{ maxHeight: '66vh' }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                {[
                  'Inv/Order',
                  'Date',
                  'Type',
                  'Table/Room',
                  'Guest',
                  'Mobile',
                  'Charged To',
                  'GST',
                  'Amount',
                  'Status',
                ].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>{o.invNumber || `ORD_${o.id}`}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {fmtDate(o.createDate)}
                  </TableCell>
                  <TableCell>{orderType(o)}</TableCell>
                  <TableCell>
                    {o.tableno || ((o.bookingId || 0) > 0 ? 'Room' : '-')}
                  </TableCell>
                  <TableCell>{o.guestName || 'Walk-in'}</TableCell>
                  <TableCell>{o.guestMobile || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size='small'
                      label={(o.bookingId || 0) > 0 ? 'Room Tab' : 'Counter'}
                      color={(o.bookingId || 0) > 0 ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {currency}
                    {Number(o.gstAmount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {currency}
                    {Number(o.finalPrice || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size='small'
                      label={
                        o.isCancel ? 'Cancelled' : o.isPaid ? 'Paid' : 'Unpaid'
                      }
                      color={
                        o.isCancel ? 'error' : o.isPaid ? 'success' : 'warning'
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align='center'>
                    No invoices found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
